import React, { useMemo, useState } from "react";
import customFetch from "./apicall/api";
import ReportHeader from "./Report_header";

function parseFilenameFromContentDisposition(contentDisposition) {
  if (!contentDisposition) return "";
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(
    contentDisposition
  );
  const filenameStar = match && match[1] ? match[1] : "";
  const filenamePlain = match && match[2] ? match[2] : "";
  const filename = decodeURIComponent(filenameStar || filenamePlain || "");
  return filename.replace(/[\\/:*?"<>|]+/g, "_");
}

function detectIdentifier(raw) {
  const value = (raw || "").trim();
  if (!value) return { isValid: false, isEmpty: true };

  // Loan ID pattern (2 letters, 3 digits, 2 letters, 6 digits, optional N + 2 digits)
  const loanIdRegex = /^[A-Za-z]{2}\d{3}[A-Za-z]{2}\d{6}(N\d{2})?$/i;
  // Proposal Number pattern(all digits, 10-20 digits)
  const proposalNumberRegex = /^\d{10,20}$/;
  // Policy Number pattern: 1 letter prefix + digits)
  const policyNumberRegex = /^[A-Za-z]\d{3,20}$/i;

  if (loanIdRegex.test(value)) {
    return { isValid: true, type: "Loan ID", queryKey: "loan_id", queryValue: value.toUpperCase() };
  }
  if (proposalNumberRegex.test(value)) {
    return { isValid: true, type: "Proposal Number", queryKey: "proposal_number", queryValue: value };
  }
  if (policyNumberRegex.test(value)) {
    return { isValid: true, type: "Policy Number", queryKey: "policy_number", queryValue: value.toUpperCase() };
  }

  return {
    isValid: false,
    isEmpty: false,
    reason: "Enter a valid Loan ID (e.g. MP009LE001858N01) / Proposal No (e.g. 2121110387628) / Policy No (e.g. A0172321).",
  };
}

function PolicyDownload(props) {
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info"); // info | error | success
  const [hasAttempted, setHasAttempted] = useState(false);

  const detection = useMemo(() => detectIdentifier(input), [input]);

  const showMessage = (type, text) => {
    setMessageType(type);
    setMessage(text);
  };

 const downloadPolicy = async () => {
  setHasAttempted(true);
  if (!detection.isValid || isLoading) return;

  setIsLoading(true);
  setMessage("");

  try {
    const apiToken = localStorage.getItem("api_token");

    const qs = new URLSearchParams({
      [detection.queryKey]: detection.queryValue,
    }).toString();

    const url = `${process.env.REACT_APP_URL}/insurance/policy/download?${qs}`;

    const res = await customFetch(url, {
      method: "GET",
      headers: {
        ...(apiToken ? { "api-token": apiToken } : {}),
      },
    });

    const contentType = res.headers.get("content-type") || "";

    // SUCCESS → backend sends PDF
    if (res.ok && contentType.includes("application/pdf")) {
      const blob = await res.blob();

      if (!blob || blob.size === 0) {
        showMessage("info", "Policy not available yet");
        return;
      }

      // Extract filename from Content-Disposition header, fallback to default
      const contentDisposition = res.headers.get("content-disposition");
      const filename = parseFilenameFromContentDisposition(contentDisposition) || `Policy_${detection.queryValue}.pdf`;

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);

      showMessage("success", "Download Completed");
      return;
    }

    // ERROR → backend sends JSON with {message, status}
    let detail = "Unable to download policy";

    if (contentType.includes("application/json")) {
      try {
        const json = await res.json();
        detail = (json && json.message) || detail;
      } catch (e) {
        // If JSON parse fails, use default message
      }
    } else {
      // Try to get text response
      try {
        detail = await res.text();
      } catch (e) {
        // Ignore
      }
    }

    // Backend returns 400/404 for: missing params, policy not found, PDF not found
    if (res.status === 400 || res.status === 404) {
      const lowerDetail = (detail || "").toLowerCase();
      if (
        lowerDetail.includes("not found") ||
        lowerDetail.includes("not available") ||
        lowerDetail.includes("pdf data not found") ||
        lowerDetail.includes("active policy")
      ) {
        showMessage("info", "Policy not available yet");
      } else {
        showMessage("error", detail || "Invalid request. Please check the identifier and try again.");
      }
    }
    else if (res.status === 500) {
      showMessage("error", detail || "Unable to download policy right now. Please try again later.");
    } 
    else {
      showMessage("error", detail || "Download failed. Please try again.");
    }

  } catch (e) {
    showMessage("error", "Network error. Please try again.");
  } finally {
    setIsLoading(false);
  }
};

  const messageClass =
    messageType === "error"
      ? "text-danger"
      : messageType === "success"
        ? "text-success"
        : "text-muted";

  const helperText = detection.isValid
    ? `Detected: ${detection.type}`
    : detection.isEmpty
      ? ""
      : detection.reason;

  const helperClass =
    detection.isValid || detection.isEmpty || !hasAttempted ? "text-muted" : "text-danger";

  return (
    <>
      {isLoading ? (
        <div className="preloader">
          <div className="loader" ng-hide="data.length > 0"></div>
        </div>
      ) : (
        ""
      )}

      <div className="container no-container">
        <ReportHeader
          title="Policy Download"
          showBack={true}
          onBack={() => props.history.goBack()}
          showLogout={true}
        />

        <div className="mt-4">
          <div className="row">
            <div className="col-md-8">
              <label className="f18">Reference Number</label>
              <div className="d-flex align-items-center" style={{ maxWidth: "500px" }}>
                <input
                  className="form-control"
                  value={input}
                  placeholder="Enter policy, loan, or proposal number"
                  onChange={(e) => {
                    setInput(e.target.value);
                    setMessage("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") downloadPolicy();
                  }}
                  disabled={isLoading}
                />
                <button
                  className={
                    !detection.isValid || isLoading
                      ? "btn btn-primary btn-height ml-3 disablesendreqbtn"
                      : "btn btn-primary btn-height ml-3"
                  }
                  style={{ minWidth: "130px", paddingTop: "0.6rem", paddingBottom: "0.6rem" }}
                  onClick={downloadPolicy}
                  disabled={!detection.isValid || isLoading}
                >
                  Download Policy
                </button>
              </div>
              <div className="mt-2">
                <span className={helperClass}>{helperText}</span>
              </div>
              {message ? <div className={`mt-2 ${messageClass}`}>{message}</div> : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PolicyDownload;

